/**
 * Files the console fetches with the signed-in person's token (PDFs, CSV exports): the bytes come
 * back as a Blob and are saved or opened in a new tab. A failed answer is read as uc-core's error
 * envelope so the person sees the server's own message.
 */
import { ApiError, buildUrl, refreshTokens, tokenStore } from '@/api'
import type { Query } from '@/api/client'

/** Save an existing Blob under `filename`. */
export function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

async function fetchFile(path: string, query?: Query): Promise<Blob> {
  const go = (token: string | null) =>
    fetch(buildUrl(path, query), { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  let res: Response
  try {
    res = await go(tokenStore.get()?.accessToken ?? null)
    if (res.status === 401) {
      const t = await refreshTokens()
      if (t) res = await go(t.accessToken)
    }
  } catch (cause) {
    throw new ApiError({ kind: 'network', message: 'Network unavailable', path, cause })
  }
  if (!res.ok) {
    const text = await res.text()
    let body: unknown = text
    try {
      body = JSON.parse(text)
    } catch {
      /* plain text */
    }
    throw ApiError.fromResponse(res.status, body, path)
  }
  return res.blob()
}

/** Save a server file under `filename`. */
export async function downloadFile(path: string, filename: string, query?: Query): Promise<void> {
  const blob = await fetchFile(path, query)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

/** Open a server file (a PDF) in a new tab. Falls back to saving it when pop-ups are blocked. */
export async function openFile(path: string, filename: string, query?: Query): Promise<void> {
  const win = window.open('', '_blank')
  try {
    const blob = await fetchFile(path, query)
    const url = URL.createObjectURL(blob)
    if (win) win.location.href = url
    else {
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (e) {
    win?.close()
    throw e
  }
}
